import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Radio,
  Select,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBuilding,
  IconMapPin,
  IconSearch,
  IconStarFilled,
} from '@tabler/icons-react';
import { bestPriceService } from '@/api/services/BestPriceService';
import { orderService } from '@/api/services/OrderService';
import { isServiceUnavailable } from '@/api/errors';
import { useBuyTree } from '@/hooks/useBuy';
import { makeOfferUrl } from '@/lib/buyLinks';
import { flattenBuyTree, groupByBasis } from '@/lib/buyTree';
import { formatMoney, formatOrgName, formatPropertyCondition, formatQualityValue } from '@/lib/format';
import { useAuth } from '@/stores/authContext';
import { ROUTES } from '@/routes/paths';
import type { BuyCptBestPrice } from '@/types/buy';
import BasisPicker from '@/components/common/BasisPicker';
import type { BasisValue } from '@/lib/basisPicker';
import { useIsAgent } from '@/hooks/useRoles';
import { useAttestationRequired } from '@/hooks/useAttestationRequired';
import NoNds from '@/components/common/NoNds';
import BestPriceLink from '@/components/common/BestPriceLink';
import BuyAttestationGate from '@/components/app/buy/BuyAttestationGate';
import IdText from '@/components/common/IdText';

// Как старый портал: грузим топ-20 одним запросом (бэкенд сортирует по цене),
// показываем ★-лучшее + по 5, остальное раскрывается клиентски.
const FETCH_LIMIT = 20;
const PAGE_SIZE = 5;

/** Зафиксированные кнопкой «Найти» параметры поиска (queryKey результата). */
interface SearchParams {
  readonly address: string;
  readonly addressTitle: string;
  readonly goodsType: string;
  readonly propertySetId: number;
  readonly prepaymentTypes: string[];
}

const PREPAYMENT_LABELS: Readonly<Record<string, string>> = {
  PERCENT_0: 'app.buy.prepayment0Short',
  PERCENT_100: 'app.buy.prepayment100Short',
};

/** Родительский товар из класса: «Пшеница 3 класс» → «Пшеница» (для ГОСТ-дефолтов приёмки). */
function parentOf(goodsClass: string, parents: readonly string[]): string {
  return (
    parents.find((parent) => goodsClass === parent || goodsClass.startsWith(`${parent} `)) ??
    goodsClass.split(' ')[0]!
  );
}

function OfferRow({ item, best, buyTo, isAgent }: {
  readonly item: BuyCptBestPrice;
  readonly best: boolean;
  readonly buyTo: string;
  /** Агент пользуется поиском справочно — действие «Купить по X» ему не показываем. */
  readonly isAgent: boolean;
}) {
  const { t } = useTranslation();
  const prepaymentKey = item.prepaymentType ? PREPAYMENT_LABELS[item.prepaymentType] : undefined;
  const total = item.weightTon != null ? item.price * item.weightTon : undefined;
  return (
    <Card
      withBorder
      padding="lg"
      bg={best ? 'lime.0' : undefined}
      style={best ? { borderColor: 'var(--mantine-color-brand-4)' } : undefined}
    >
      <Stack gap="sm">
        {best ? (
          <Group gap={6}>
            <IconStarFilled size={16} color="var(--mantine-color-wheat-6)" />
            <Text fw={700} c="brand.9">
              {t('app.buy.search.bestOffer')}
            </Text>
          </Group>
        ) : null}
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={6} style={{ minWidth: 0, flex: 1 }}>
            <Group gap={6} wrap="nowrap">
              <IconBuilding size={16} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
              <Text fw={600} style={{ minWidth: 0 }}>
                {formatOrgName(item.sellerName) ?? '—'}
              </Text>
              {item.sellerInn ? (
                <Text size="sm" c="gray.7" style={{ flexShrink: 0 }}>
                  · {t('auth.fields.inn')} <IdText>{item.sellerInn}</IdText>
                </Text>
              ) : null}
            </Group>
            <Group gap={6} wrap="nowrap">
              <IconMapPin size={16} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
              <Text size="sm" c="gray.8">
                {formatOrgName(item.addressTitle) ?? item.sellerAddress ?? '—'}
              </Text>
            </Group>
            <Group gap={4} wrap="wrap">
              {item.propertyName ? (
                <Badge variant="default" tt="none" fw={500} radius="sm" c="gray.9">
                  {item.propertyName}
                  {item.propertyValue != null ? ` ${formatQualityValue(item.propertyValue)}` : ''}
                  {item.propertyPostfix ?? ''}
                </Badge>
              ) : null}
              {prepaymentKey ? (
                <Badge variant="default" tt="none" fw={500} radius="sm" c="gray.9">
                  {t(prepaymentKey)}
                </Badge>
              ) : null}
              {item.weightTon != null ? (
                <Badge variant="default" tt="none" fw={500} radius="sm" c="gray.9">
                  {formatMoney(item.weightTon)} т
                </Badge>
              ) : null}
            </Group>
          </Stack>
          <Stack gap={4} align="flex-end">
            <Group gap={6} align="baseline">
              <Text fz={best ? 28 : 22} fw={700} c="brand.9">
                {formatMoney(item.price)}
              </Text>
              <Text size="sm" c="gray.8">
                {t('app.buy.offer.rubPerTon')}
              </Text>
            </Group>
            {total != null ? (
              <Text size="sm" c="gray.8">
                {t('app.buy.offer.total')}:{' '}
                <Text component="span" fw={600} c="gray.9">
                  {formatMoney(total)} ₽
                </Text>
                <NoNds />
              </Text>
            ) : null}
            {!isAgent ? <BestPriceLink to={buyTo} price={item.price} action="buy" tone={best ? 'gain' : 'plain'} /> : null}
          </Stack>
        </Group>
      </Stack>
    </Card>
  );
}

// Экран «Лучшая цена» (Фаза 4): поиск лучших предложений продавцов с доставкой до МОЕГО
// базиса (точки выгрузки берутся из моих прайс-листов). Качество — основной параметр
// культуры (первое свойство наборов goods_propersties; для пшеницы — протеин).
export default function BestPriceSearchPage() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const isAgent = useIsAgent(); // агенту в результатах не показываем CTA «Купить по X» (только справочно)
  // Агента аттестация не касается (он не аттестуется и пользуется поиском справочно) — как и в
  // остальных гейтах платформы, исключаем его. Иначе агент с isVerified=false/undefined был бы заблокирован.
  const attestationBlocked = useAttestationRequired() && !isAgent;

  const [basis, setBasis] = useState<BasisValue | null>(null);
  const [goodsType, setGoodsType] = useState<string | null>(null);
  const [propertySetId, setPropertySetId] = useState<string | null>(null);
  const [prepayment, setPrepayment] = useState<string>('ANY');
  const [submitted, setSubmitted] = useState<SearchParams | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Мои базисы (точки выгрузки) — из дерева закупок; доставка считается до них. В пикере это группа
  // «Мои базисы»; рядом — поиск любого адреса по справочнику.
  const tree = useBuyTree();
  const myBases = useMemo(() => groupByBasis(flattenBuyTree(tree.data)), [tree.data]);
  const ownBases = useMemo(
    () => myBases.map((base) => ({ address: base.address, addressTitle: base.addressTitle })),
    [myBases],
  );

  const goodsClasses = useQuery({
    queryKey: ['goodsClasses'] as const,
    queryFn: ({ signal }) => orderService.getGoodsClasses(signal),
    enabled: isAuthenticated && !attestationBlocked,
    staleTime: 10 * 60_000,
  });

  const parentGoodsTypes = useQuery({
    queryKey: ['parentGoodsTypes'] as const,
    queryFn: ({ signal }) => orderService.getParentGoodsTypes(signal),
    enabled: isAuthenticated && !attestationBlocked,
    staleTime: 10 * 60_000,
  });

  const propertySets = useQuery({
    queryKey: ['goodsPropertySets', goodsType] as const,
    queryFn: ({ signal }) => orderService.getGoodsPropertySets(goodsType!, signal),
    enabled: isAuthenticated && !!goodsType,
    staleTime: 10 * 60_000,
  });

  const results = useQuery({
    queryKey: ['bestPriceSearch', submitted] as const,
    queryFn: ({ signal }) =>
      bestPriceService.getCptForAddress(
        {
          address: submitted!.address,
          goodsType: submitted!.goodsType,
          propertySetId: submitted!.propertySetId,
          prepaymentTypes: submitted!.prepaymentTypes,
          limit: FETCH_LIMIT,
          offset: 0,
        },
        signal,
      ),
    enabled: isAuthenticated && !!submitted,
    staleTime: 30_000,
    retry: false,
  });

  // Основной параметр культуры — первое свойство наборов (для пшеницы — «Протеин», паттерн
  // старого портала). Опция = его значение («≥ 15.3 %»), значение опции = propsSetId набора.
  const mainPropertyName = propertySets.data?.[0]?.properties?.[0]?.name;
  const propertyData = useMemo(
    () =>
      (propertySets.data ?? [])
        .filter((set) => set.propsSetId != null && set.properties?.[0]?.value != null)
        .map((set) => {
          const property = set.properties![0]!;
          return { value: String(set.propsSetId), label: formatPropertyCondition(property) };
        }),
    [propertySets.data],
  );

  const canSearch = !!basis && !!goodsType && !!propertySetId;

  const search = () => {
    if (!canSearch) {
      return;
    }
    setVisibleCount(PAGE_SIZE);
    setSubmitted({
      address: basis!.address,
      addressTitle: basis!.addressTitle,
      goodsType: goodsType!,
      propertySetId: Number(propertySetId),
      prepaymentTypes: prepayment === 'ANY' ? ['PERCENT_0', 'PERCENT_100'] : [prepayment],
    });
  };

  const items = results.data ?? [];
  const best = items[0];
  const rest = items.slice(1, 1 + visibleCount);
  const hasMore = items.length > 1 + visibleCount;

  const buyUrl = (item: BuyCptBestPrice): string =>
    makeOfferUrl({
      entry: { price: item.price, saleOrderId: item.saleOrderId, saleOrderVersion: item.saleOrderVersion },
      toAddress: submitted!.address,
      toAddressTitle: submitted!.addressTitle,
      goods: parentOf(submitted!.goodsType, parentGoodsTypes.data ?? []),
    });

  // Заголовок раздела (назад + иконка + название) — общий для обычного вида и экрана-ограничения.
  const header = (
    <Stack gap="xs">
      {/* Навигация: раздел живёт рядом с прайс-листами — даём явный путь назад. */}
      <Anchor component={Link} to={ROUTES.APP_BUY} size="sm" c="brand.7">
        <Group gap={4} wrap="nowrap">
          <IconArrowLeft size={16} />
          {t('app.buy.search.toPriceLists')}
        </Group>
      </Anchor>
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color="brand" size={44} radius="md" style={{ flexShrink: 0 }}>
          <IconSearch size={26} />
        </ThemeIcon>
        <Stack gap={4}>
          <Title order={2}>{t('app.buy.search.title')}</Title>
          <Text c="gray.8">{t('app.buy.search.subtitle')}</Text>
        </Stack>
      </Group>
    </Stack>
  );

  // До аттестации закупочная «Лучшая цена» недоступна — единый стиль с /app/sell/best-price и запретом
  // создания прайс-листов. Заголовок сохраняем, поиск заменяем баннером-ограничением.
  if (attestationBlocked) {
    return (
      <Container size="md" py="xl">
        <Stack gap="lg">
          {header}
          <BuyAttestationGate />
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        {header}

        {/* Фильтры — карточка сверху (дизайн §6): мой базис, культура с классом, качество, предоплата. */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            <Group grow align="flex-start" preventGrowOverflow={false} wrap="wrap">
              <BasisPicker
                value={basis}
                onChange={setBasis}
                ownBases={ownBases}
                label={t('app.buy.search.address')}
                placeholder={t('app.buy.search.addressPlaceholder')}
                miw={260}
              />
              <Select
                label={t('app.buy.search.goods')}
                placeholder={t('app.buy.form.goodsPlaceholder')}
                data={goodsClasses.data ?? []}
                value={goodsType}
                onChange={(value) => {
                  setGoodsType(value);
                  setPropertySetId(null);
                }}
                searchable
                miw={220}
              />
              <Select
                label={mainPropertyName ?? t('app.buy.search.quality')}
                placeholder={
                  goodsType ? t('app.buy.search.qualityPlaceholder') : t('app.buy.search.qualityFirst')
                }
                data={propertyData}
                value={propertySetId}
                onChange={setPropertySetId}
                disabled={!goodsType || propertySets.isLoading}
                miw={180}
              />
            </Group>
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
              <Radio.Group value={prepayment} onChange={setPrepayment} label={t('app.buy.search.prepayment')}>
                <Group gap="lg" mt={6}>
                  <Radio value="ANY" label={t('app.buy.search.prepaymentAny')} />
                  <Radio value="PERCENT_0" label={t('app.buy.prepayment0Short')} />
                  <Radio value="PERCENT_100" label={t('app.buy.prepayment100Short')} />
                </Group>
              </Radio.Group>
              <Button
                onClick={search}
                disabled={!canSearch}
                loading={results.isFetching}
                leftSection={<IconSearch size={18} />}
              >
                {t('app.buy.search.find')}
              </Button>
            </Group>
          </Stack>
        </Card>


        {!submitted ? (
          <Card withBorder padding="xl">
            <Stack gap={4} align="center">
              <Text fw={600}>{t('app.buy.search.hintTitle')}</Text>
              <Text c="gray.8" ta="center">
                {t('app.buy.search.hintText')}
              </Text>
            </Stack>
          </Card>
        ) : results.isError ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
            <Group justify="space-between" wrap="nowrap">
              <Text>
                {isServiceUnavailable(results.error)
                  ? t('app.buy.search.unavailable')
                  : // 500 на этом эндпоинте почти всегда = не рассчиталась доставка до базиса
                    // (нет дистанции и недоступен сервис расчёта) — говорим осмысленно.
                    t('app.buy.search.failed')}
              </Text>
              <Button variant="subtle" size="compact-sm" onClick={() => results.refetch()}>
                {t('common.retry')}
              </Button>
            </Group>
          </Alert>
        ) : results.isLoading ? (
          <Stack gap="sm">
            <Skeleton h={150} />
            <Skeleton h={110} />
            <Skeleton h={110} />
          </Stack>
        ) : items.length === 0 ? (
          <Card withBorder padding="xl">
            <Stack gap={4} align="center">
              <Text fw={600}>{t('app.buy.search.emptyTitle')}</Text>
              <Text c="gray.8" ta="center">
                {t('app.buy.search.emptyText')}
              </Text>
            </Stack>
          </Card>
        ) : (
          <Stack gap="sm">
            {best ? <OfferRow item={best} best buyTo={buyUrl(best)} isAgent={isAgent} /> : null}
            {rest.map((item) => (
              <OfferRow
                key={`${item.saleOrderId}-${item.prepaymentType ?? ''}`}
                item={item}
                best={false}
                buyTo={buyUrl(item)}
                isAgent={isAgent}
              />
            ))}
            {hasMore ? (
              <Button
                variant="default"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                style={{ alignSelf: 'center' }}
              >
                {t('app.buy.search.showMore')}
              </Button>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
